import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import type {
    CreateB2BLeadMutation,
    CreateB2BLeadMutationVariables,
} from 'generated/graphql';
import { CreateB2BLeadDocument } from 'generated/graphql';
import { leadFormSchema } from '@modules/b2b/lead-form/lead-schema';

/**
 * Endpoint for the B2B "Apply to Join" lead form. Import this constant in the
 * client instead of hardcoding the path.
 */
export const B2B_LEAD_API_ROUTE = '/remix-api/b2b/lead';

const PAGE_NAME = 'B2B Apply to Join';

/**
 * Validates the submitted lead with the shared zod schema, then forwards it to the
 * `createB2BLead` mutation. That mutation queues an async job which POSTs to the
 * configured HubSpot form (fire-and-forget) — so a successful response here means the
 * submission was accepted and queued, not that HubSpot has finished processing it.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const formData = await request.formData();
    const parsed = leadFormSchema.safeParse(Object.fromEntries(formData));

    if (!parsed.success) {
        return json(
            { ok: false, errors: parsed.error.flatten().fieldErrors },
            { status: 400 },
        );
    }

    const lead = parsed.data;

    // HubSpot tracking cookie, if present. The form is not HubSpot-embedded, so this is
    // usually absent — HubSpot treats hutk as optional.
    const hutk = request.headers
        .get('cookie')
        ?.match(/hubspotutk=([^;]+)/)?.[1];

    try {
        const { data } = await context.client.mutate<
            CreateB2BLeadMutation,
            CreateB2BLeadMutationVariables
        >({
            mutation: CreateB2BLeadDocument,
            variables: {
                Input: {
                    FirstName: lead.firstName,
                    LastName: lead.lastName,
                    BusinessName: lead.businessName,
                    BusinessWebsite: lead.businessWebsite,
                    Email: lead.email,
                    Phone: lead.phone,
                    JobTitle: lead.jobTitle,
                    Industry: lead.industry,
                    State: lead.state,
                    City: lead.city,
                    Message: lead.message ?? '',
                },
                hubspotContext: {
                    hutk: hutk ?? '',
                    pageUri: request.headers.get('referer') ?? '',
                    pageName: PAGE_NAME,
                },
            },
        });

        return json({ ok: data?.createB2BLead?.Success ?? false });
    } catch (err) {
        context.logger.error('[b2b-lead] submission failed', err);
        return json({ ok: false }, { status: 500 });
    }
};
